import { ReactNode } from 'react';
import { Trans } from '@lingui/react/macro';

import Stack from '@/design_system/Stack';
import { ArticleWithRelations } from '@/models/request';
import { hasPermission } from '@/models/user';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { PendingClientActionReminder } from '@/routes/Requests/Request/components/PendingClientActionReminder/PendingClientActionReminder';
import { useCurrentSession } from '@/services/auth';

import { useArticleContext } from '../../../../contexts/ArticleContext';

import { RecordClientContactMessage } from './shared/RecordClientContactMessage';
import { BaseMessage } from './BaseMessage';

export const ValidationStepMessage = () => {
  const { article, request } = useArticleContext();

  if (!article || article.step?.step !== 'validation') {
    return null;
  }

  const isClientCommunicationEnabled =
    !!request.organization.config.clientCommunication?.notifications?.enabled;
  const isValidationOnBehalfOfClient = article.step.config.actOnBehalfOfClient;

  if (isValidationOnBehalfOfClient) {
    return <ValidationStepMessageOnBehalfOfClient />;
  }

  if (isClientCommunicationEnabled) {
    return <ValidationStepMessageByClient article={article} />;
  }
};

// This step message is displayed on the Article page
// and on the Request page as well if at least one Article has it on its page.
// For this reason, it needs `article` as prop
// as on the Request page we can't useArticleContext
export const ValidationStepMessageByClient = ({ article }: { article: ArticleWithRelations }) => {
  const { currentSession } = useCurrentSession();
  const { request } = useRequestContext();

  const isValidating = !article.validationCompletedAt;

  const hasValidatedAndIsInStoreDropoff =
    !!article.quoteAcceptedAt &&
    !!article.validationCompletedAt &&
    !request.store &&
    !!request.client &&
    article.step?.step === 'validation' &&
    !!article.step.config.requireShippingChoice &&
    request.client?.shippingChoice === 'store';

  const canSendValidationReminder =
    request.organization.config.clientCommunication?.notifications?.enabled &&
    isValidating &&
    hasPermission(currentSession, 'write_client_communication', [
      {
        organizationId: request.organizationId,
        entityId: null,
      },
      {
        entityId: request.afterSalesId,
      },
      {
        entityId: request.storeId,
      },
    ]);

  let messageContent: ReactNode = null;

  if (isValidating) {
    if (canSendValidationReminder) {
      messageContent = (
        <Trans id="request.step.validation.text.waiting-reminder">
          A quote has been submitted and is waiting for validation. If the client doesn't respond,
          feel free to send them an email reminder.
        </Trans>
      );
    } else {
      messageContent = (
        <Trans id="request.step.validation.text.waiting">
          A quote has been submitted and is waiting for validation.
        </Trans>
      );
    }
  } else if (hasValidatedAndIsInStoreDropoff) {
    messageContent = (
      <Trans id="request.step.validation.text.store-drop-off">
        The client has validated the quote. They now need to bring the items to a store.
      </Trans>
    );
  }

  return (
    <BaseMessage
      title={<Trans id="request.step.validation.title.waiting">Waiting for validation</Trans>}
      message={
        <Stack gap="0.5rem">
          {messageContent}
          {canSendValidationReminder && (
            <PendingClientActionReminder id={request.id} type="validation" />
          )}
        </Stack>
      }
      article={article}
    />
  );
};

const ValidationStepMessageOnBehalfOfClient = () => {
  const { currentSession } = useCurrentSession();
  const { article, request } = useArticleContext();

  const contactClientTaskEnabled =
    article.step?.step === 'validation' && article.step.config.tasks?.contact_client?.enabled;
  const canValidateOnBehalfOfClient = hasPermission(currentSession, 'write_client_communication', {
    entityId: request.storeId,
  });

  return (
    <BaseMessage
      title={
        canValidateOnBehalfOfClient ? (
          <Trans id="request.step.validation.title.act">Validate the quote</Trans>
        ) : (
          <Trans id="request.step.validation.title.waiting">Waiting for validation</Trans>
        )
      }
      message={
        <Stack gap="0.5rem" alignItems="flex-start">
          {canValidateOnBehalfOfClient ? (
            <>
              <p>
                <Trans id="request.step.validation.text.act">
                  The quote is awaiting validation, please validate or refuse it.
                </Trans>
              </p>
              {contactClientTaskEnabled && <RecordClientContactMessage />}
            </>
          ) : (
            <>
              <Trans id="request.step.validation.text.waiting">
                A quote has been submitted and is waiting for validation.
              </Trans>
            </>
          )}
        </Stack>
      }
      article={article}
    />
  );
};
